// Canonieke feature-registry voor de per-school feature-flags.
//
// Eén bron van waarheid voor: menu-filtering (Sidebar), route-guards (FeatureRoute),
// en het admin-beheerscherm. Een feature is AAN tenzij een school 'm expliciet op
// false zet in scholen.features (JSONB). Zie migrations/014_school_features.sql.
//
// - key: sleutel in scholen.features
// - label/description: voor het beheerscherm
// - navIds: Sidebar-menu-id's die deze feature toont/verbergt
// - routes: paden die achter deze feature vallen (voor FeatureRoute)
// - pakket (13-10-2026, Yasar): 'basis' zit bij elke school in het
//   standaardpakket; 'extra' is een add-on die TestWisely per school aanzet.
//   Alleen voor de docentkant; het leerlingportaal heeft zijn eigen lijst.

export const FEATURES = [
  {
    key: 'communicatie',
    label: 'Communicatiesessies',
    description: 'Peilingen, stemmingen en open vragen tijdens de les.',
    navIds: ['communicatie'],
    routes: ['/communicatie-page', '/nieuwe-communicatie', '/communicatie-snel', '/statistieken-communicatie'],
    // 12-09-2026: schakelbaar per school én per docent (geen school had 'm uit).
    gated: true,
    pakket: 'basis',
  },
  {
    // Analyse is geen schakelbare functie (altijd aan), maar hoort in het
    // overzicht van het basispakket thuis (13-10-2026).
    key: 'analyse',
    label: 'Analyse',
    description: 'Analyse van resultaten per klas en per leerling.',
    navIds: [],
    routes: [],
    gated: false,
    pakket: 'basis',
  },
  {
    key: 'toetsen',
    label: 'Toetsen',
    description: 'Toetsen maken, afnemen en beheren.',
    navIds: ['toetsen'],
    routes: ['/toetsen-page'],
    gated: true,
    pakket: 'extra',
  },
  {
    key: 'vragenbank',
    label: 'Vragenbank',
    description: 'Eigen vragenbank met herbruikbare vragen.',
    navIds: ['vragenbank'],
    routes: ['/vragenbank'],
    gated: true,
    pakket: 'basis',
  },
  {
    key: 'coaching',
    label: 'Coaching',
    description: 'Coachgesprekken-module (dashboard + beheer).',
    navIds: ['coaching', 'coachingBeheer'],
    routes: ['/coaching'],
    gated: true,
    pakket: 'extra',
  },
  {
    key: 'leerdoelen',
    label: 'Leerdoelen',
    description: 'Leerdoelenbank en leerdoel-koppeling.',
    navIds: ['leerdoelen'],
    routes: ['/leerdoelen'],
    gated: true,
    pakket: 'basis',
  },
  {
    // Eigen schakelaar (14-10-2026, Yasar): Leerkaarten is een losse functie
    // in het basispakket (15-10-2026). Zoals alle vlaggen aan tenzij de school hem op false zet
    // (scholen.features JSONB, geen migratie nodig).
    key: 'leerkaarten',
    label: 'Leerkaarten',
    description: 'Woorden, zinnen en begrippen uit de boeken per hoofdstuk, als toets, communicatiesessie of om zelf te oefenen.',
    // Ook het leerlingportaal (Leerkaarten-tab, /student/leerkaarten) volgt
    // deze sleutel; zie NAV_FEATURES in student-portal/StudentShell.js.
    navIds: ['leerkaarten'],
    routes: ['/leerkaarten', '/student/leerkaarten'],
    gated: true,
    pakket: 'basis',
  },
  {
    key: 'boekenplank',
    label: 'Boekenplank',
    description: 'Lesmateriaal-integratie en boek-gedreven vragen (Noordhoff).',
    navIds: ['boekenplank'],
    routes: ['/boekenplank'],
    gated: true,
    pakket: 'extra',
  },
  {
    key: 'lesmodus',
    label: 'Lesmodus',
    description: 'Leerlingen AAC-vergrendeld in een les met links/PowerPoints; exit via docent-wachtwoord of timer.',
    // Externe toets (DIA e.d. in de iPad-app op volledig scherm) hoort bij
    // dezelfde schoolvlag: wie lesmodus heeft, heeft ook externe toetsen.
    navIds: ['lesmodus', 'externeToets'],
    routes: ['/lesmodus', '/externe-toets'],
    gated: true,
    pakket: 'extra',
  },
  {
    key: 'mentoruur',
    label: 'Mentoruur (school-brede lessen)',
    description: 'Vakdocenten zetten lesmodus-opdrachten klaar voor klassen van collega\'s, met geplande activatie (bijv. tijdens het mentoruur).',
    navIds: [],
    routes: [],
    gated: true,
    pakket: 'extra',
  },
  {
    key: 'agenda',
    label: 'Agenda en rooster',
    description: 'Rooster en afspraken voor docent en leerling.',
    navIds: ['agenda'],
    routes: ['/agenda-page'],
    gated: true,
    pakket: 'extra',
  },
  // Onderdelen van het leerlingportaal (07-09-2026). Ze stonden wel in
  // scholen.features maar niet in deze lijst, dus waren ze alleen met de hand
  // in de database aan of uit te zetten. Nu horen ze gewoon in het beheerscherm.
  {
    key: 'cijfers',
    label: 'Cijfers (leerling)',
    description: 'De leerling ziet zijn eigen cijfers per vak.',
    navIds: [],
    routes: ['/student/resultaten'],
    gated: true,
  },
  {
    key: 'voortgang',
    label: 'Voortgang (leerling)',
    description: 'Trend per vak en beheersing per hoofdstuk voor de leerling.',
    navIds: [],
    routes: ['/student/voortgang'],
    gated: true,
  },
  {
    key: 'leerstof',
    label: 'Leerstof (leerling)',
    description: 'Lessen van de docent en waar de leerling aan werkte.',
    navIds: [],
    routes: ['/student/leerstof'],
    gated: true,
  },
  {
    key: 'klassen',
    label: 'Klassen (leerling)',
    description: 'De leerling ziet in welke klassen hij zit.',
    navIds: [],
    routes: ['/student/klassen'],
    gated: true,
  },
  {
    key: 'ai_nakijken',
    label: 'AI-nakijken',
    description: 'Automatisch nakijken en genereren van vragen via AI.',
    navIds: [],
    routes: [],
    gated: true,
    pakket: 'extra',
  },
];

// Docentfuncties per pakket, voor het overzicht in Schoolbeheer > School.
export const basisFuncties = () => FEATURES.filter(f => f.pakket === 'basis');
export const extraFuncties = () => FEATURES.filter(f => f.pakket === 'extra');

// Het basispakket staat bij elke school altijd aan (26-10-2026, Yasar). Op schoolniveau
// kan het niet uit; een false in scholen.features telt voor deze sleutels niet.
// Zelfde lijst als BASIS_SLEUTELS in backend/src/middleware/requireFunctie.js.
export const BASIS_KEYS = FEATURES.filter(f => f.pakket === 'basis').map(f => f.key);

// Keys die daadwerkelijk gegate worden.
export const GATED_KEYS = FEATURES.filter(f => f.gated).map(f => f.key);

// Functies die bij lesgeven horen (13-10-2026, migratie 074). Een
// schoolbeheerder zonder lestaak (docenten.geeft_les = false) heeft ze
// allemaal uit, wat de school ook aan heeft. 'coaching' staat er niet bij:
// die sleutel dekt ook Coaching · beheer, dat een beheerder juist wél heeft;
// de docentkant van coaching wordt per menu-item en route geregeld. De
// backend heeft dezelfde lijst (mét coaching) in middleware/requireFunctie.js.
export const LESFUNCTIES = ['toetsen', 'communicatie', 'lesmodus', 'mentoruur', 'leerdoelen', 'leerkaarten', 'vragenbank'];

// Beheerder zonder lestaak: rol schoolbeheerder met docenten.geeft_les = false.
// Krijgt een eigen dashboard en zijbalk en heeft alle LESFUNCTIES uit.
// Zelfde regel als isAlleenBeheer in backend/src/middleware/requireFunctie.js.
export function isAlleenBeheer(profiel) {
  return !!profiel && profiel.rol === 'schoolbeheerder' && profiel.geeft_les === false;
}

// Menu-id → feature-key (voor Sidebar-filtering).
export const NAV_FEATURE_MAP = FEATURES.reduce((acc, f) => {
  if (f.gated) f.navIds.forEach(id => { acc[id] = f.key; });
  return acc;
}, {});
